import { useState } from 'react'
import { Gauge, History, Radio, Wifi } from 'lucide-react'
import { Button, Badge } from '../ui'
import { formatRoamingTime, simulateClientSpeedTest } from '../../lib/floorPlan/clientTelemetry'
import { formatClientRssi, rssiClientColor } from '../../lib/floorPlan/floorClients'
import type { ClientSpeedTestResult, FloorClientDot } from '../../lib/floorPlan/types'

type Props = {
  client: FloorClientDot
  onRunSpeedTestApi?: (clientId: string, rssi: number) => Promise<ClientSpeedTestResult>
  onSpeedTestComplete?: (clientId: string, result: ClientSpeedTestResult) => void
}

export default function FloorPlanClientPanel({
  client,
  onRunSpeedTestApi,
  onSpeedTestComplete,
}: Props) {
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const result = client.lastSpeedTest

  async function runSpeedTest() {
    setTesting(true)
    setError(null)
    try {
      const out = onRunSpeedTestApi
        ? await onRunSpeedTestApi(client.id, client.rssi)
        : await simulateClientSpeedTest(client.rssi)
      onSpeedTestComplete?.(client.id, out)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speed test failed')
    } finally {
      setTesting(false)
    }
  }

  const rssiColor = rssiClientColor(client.rssi, client.sticky)

  return (
    <div className="space-y-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-mg-text">Wireless client</p>
          <p className="text-sm font-medium text-mg-text">{client.label ?? client.id}</p>
        </div>
        <Badge variant={client.sticky ? 'warning' : client.rssi >= -65 ? 'success' : client.rssi >= -75 ? 'warning' : 'danger'}>
          {formatClientRssi(client.rssi)}
        </Badge>
      </div>

      {client.locationEstimated && (
        <p className="text-[10px] text-mg-muted">
          Map position is estimated near associated AP (typical when controller reports association + RSSI only).
        </p>
      )}

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
        <dt className="text-mg-muted">Associated AP</dt>
        <dd className="font-medium text-mg-text">{client.ap}</dd>
        <dt className="text-mg-muted">MAC</dt>
        <dd className="font-mono text-[10px] text-mg-text">{client.mac ?? client.id}</dd>
        <dt className="text-mg-muted">Device</dt>
        <dd className="text-mg-text">{client.deviceType ?? 'Unknown'}</dd>
        <dt className="text-mg-muted">IP</dt>
        <dd className="font-mono text-[10px] text-mg-text">{client.ipAddress ?? '—'}</dd>
        <dt className="text-mg-muted">SSID · Band</dt>
        <dd className="text-mg-text">
          {client.ssid ?? '—'} · {client.band ?? '—'}
        </dd>
        <dt className="text-mg-muted">Connected since</dt>
        <dd className="text-mg-text">
          {client.connectedSince ? formatRoamingTime(client.connectedSince) : '—'}
        </dd>
      </dl>

      <div className="space-y-2 border-t border-mg-border/60 pt-2">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-mg-muted">
          <History size={12} /> Roaming history
        </p>
        <ul className="max-h-40 space-y-1.5 overflow-y-auto">
          {(client.roamingHistory ?? []).map((ev, idx) => (
            <li key={`${ev.at}-${idx}`} className="rounded-md border border-mg-border bg-white/60 px-2 py-1.5 text-[10px]">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-mg-text">{ev.ap}</span>
                <span className="tabular-nums text-mg-muted">{ev.rssi} dBm</span>
              </div>
              <p className="mt-0.5 text-mg-muted">
                {formatRoamingTime(ev.at)}
                {ev.reason ? ` · ${ev.reason}` : ''}
              </p>
            </li>
          ))}
          {(client.roamingHistory ?? []).length === 0 && (
            <li className="text-[10px] text-mg-muted">No roaming events recorded.</li>
          )}
        </ul>
      </div>

      <div className="space-y-2 border-t border-mg-border/60 pt-2">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-mg-muted">
          <Gauge size={12} /> Speed test
        </p>
        <Button variant="secondary" size="sm" className="w-full" loading={testing} onClick={() => void runSpeedTest()}>
          <Wifi size={14} />
          Run speed test from client
        </Button>
        {error && <p className="text-[10px] text-mg-danger">{error}</p>}
        {result && (
          <div className="grid grid-cols-2 gap-2 rounded-md border border-mg-border bg-mg-page p-2 text-[11px]">
            <div>
              <p className="text-mg-muted">Download</p>
              <p className="font-semibold tabular-nums text-mg-text">{result.downloadMbps} Mbps</p>
            </div>
            <div>
              <p className="text-mg-muted">Upload</p>
              <p className="font-semibold tabular-nums text-mg-text">{result.uploadMbps} Mbps</p>
            </div>
            <div>
              <p className="text-mg-muted">Latency</p>
              <p className="font-semibold tabular-nums text-mg-text">{result.latencyMs} ms</p>
            </div>
            <div>
              <p className="text-mg-muted">Jitter / loss</p>
              <p className="font-semibold tabular-nums text-mg-text">
                {result.jitterMs} ms · {result.packetLossPct}%
              </p>
            </div>
            <p className="col-span-2 text-[10px] text-mg-muted">
              {formatRoamingTime(result.at)} · modeled from current RSSI ({client.rssi} dBm)
            </p>
          </div>
        )}
      </div>

      <p className="flex items-center gap-1 text-[10px] text-mg-muted">
        <Radio size={10} style={{ color: rssiColor }} />
        Signal quality follows live RF geometry when APs or walls change.
      </p>
    </div>
  )
}
